/**
 * Iter 97.11 — Commercials Builder consumes the Products & Services
 * catalogue as its single source of master data.
 *
 * Architectural shift:
 *   - PRE — A hardcoded 3-level hierarchy lived in this file
 *           (Business Category → Product → Module).
 *   - POST — The hierarchy is hydrated at runtime from the catalogue
 *            API (`GET /api/billing/catalogue/list?kind=product
 *            &active_only=true`). Operators maintain Products from the
 *            existing /catalogue admin page.
 *
 * Compatibility:
 *   - The exported `PRODUCT_MASTER` constant is preserved (consumers
 *     keep importing it). It starts EMPTY and is mutated in-place by
 *     `hydrateProductMasterFromCatalogue` so existing imports continue
 *     pointing at the live array reference.
 *   - The exported `HOSTING_GROUP_CODE` and `flattenSelectableLeaves`
 *     helpers are unchanged.
 *
 * Capability ↔ Delivery-mode mapping:
 *   - Catalogue uses `web` (CAPABILITY_SUGGESTIONS includes web/android/
 *     ios/api/backoffice/white_label/...). Builder uses
 *     `browser/android/ios/api/data_feed`. The mapper aliases `web →
 *     browser` and passes through the rest; unknown codes are dropped.
 */

import { api } from "@/lib/api";

/**
 * Live hierarchy consumed by every Builder section. Starts EMPTY —
 * `hydrateProductMasterFromCatalogue` mutates this array in place so
 * existing static imports continue to read the current data without
 * needing a re-import after fetch.
 */
export const PRODUCT_MASTER = [];

/* Hosting bucket code — unchanged from the pre-97.11 hierarchy. */
export const HOSTING_GROUP_CODE = "hosting";

/* Hydration lifecycle — exported so the Builder root can render an
   appropriate state while the catalogue fetch is in flight. */
let _state = "idle"; // "idle" | "loading" | "ready" | "error"
let _error = null;
const _listeners = new Set();

export function getHydrationState() {
    return { state: _state, error: _error };
}

export function subscribeToHydration(listener) {
    _listeners.add(listener);
    return () => _listeners.delete(listener);
}

function _emit() {
    _listeners.forEach((l) => { try { l({ state: _state, error: _error }); } catch (_) { /* swallow */ } });
}

/* Capability → Delivery-mode aliases. Keep aligned with DELIVERY_MODES
   in builderStorage.js so the icon set in the print preview stays in
   sync with operator selection. */
const CAPABILITY_TO_DELIVERY_MODE = {
    web: "browser",
    browser: "browser",
    android: "android",
    ios: "ios",
    api: "api",
    data_feed: "data_feed",
    datafeed: "data_feed",
    feed: "data_feed",
};

const VALID_DELIVERY_MODES = new Set([
    "browser", "android", "ios", "api", "data_feed",
]);

function _mapCapabilitiesToDeliveryModes(capabilities) {
    const seen = new Set();
    const out = [];
    (capabilities || []).forEach((c) => {
        const code = CAPABILITY_TO_DELIVERY_MODE[String(c).toLowerCase()] || String(c).toLowerCase();
        if (VALID_DELIVERY_MODES.has(code) && !seen.has(code)) {
            seen.add(code);
            out.push(code);
        }
    });
    return out;
}

/**
 * Transform a flat list of catalogue products into the 3-level
 * Builder hierarchy (Business Category → Product → Module).
 * Products without a `category` become top-level standalone Products.
 */
function _buildHierarchy(catalogueItems) {
    const sorted = [...(catalogueItems || [])].sort(
        (a, b) => (a.sort_order ?? 100) - (b.sort_order ?? 100),
    );
    const categoryOrder = []; // preserve first-seen order
    const categoryProducts = new Map();
    const standalone = [];

    sorted.forEach((item) => {
        // Iter 97.17 — Prefer the first-class `default_delivery_modes`
        // field when populated; fall back to the capabilities → delivery
        // alias mapping for legacy rows that pre-date Iter 97.17.
        const explicitModes = Array.isArray(item.default_delivery_modes)
            ? item.default_delivery_modes.filter((c) => VALID_DELIVERY_MODES.has(String(c).toLowerCase()))
            : [];
        const productNode = {
            type: "product",
            code: item.code,
            label: item.name,
            description: item.subtitle || "",
            // UAT 45.6.5 — carry through the sort_order so the outer
            // hierarchy assembler can interleave categories + standalones
            // by sort_order. Stripped before the node is pushed to
            // PRODUCT_MASTER so downstream consumers see the same shape
            // as before.
            sortOrder: item.sort_order ?? 100,
            defaultDeliveryModes: explicitModes.length > 0
                ? explicitModes
                : _mapCapabilitiesToDeliveryModes(item.capabilities),
            modules: (item.builder_modules || []).map((m) => ({
                code: m.code,
                label: m.label,
            })),
        };
        if (item.mutually_exclusive) {
            productNode.mutuallyExclusive = true;
            // Default-select the first module as the chosen exclusive option.
            if (productNode.modules.length > 0) {
                productNode.defaultModuleCode = productNode.modules[0].code;
            }
        }
        if (item.no_delivery_modes) {
            productNode.noDeliveryModes = true;
        }

        const cat = (item.category || "").trim();
        if (!cat) {
            standalone.push(productNode);
        } else {
            if (!categoryProducts.has(cat)) {
                categoryOrder.push(cat);
                categoryProducts.set(cat, []);
            }
            categoryProducts.get(cat).push(productNode);
        }
    });

    const hierarchy = [];
    // UAT 45.6.5 (2026-07-14) — Interleave categories and standalone
    // products by sort_order (category sort_order = min(sort_order) of
    // its children). Previously categories rendered as a block before
    // any standalone product, which broke the operator's ability to
    // position a standalone (e.g. aiM-CAS at sort_order=75) directly
    // below a specific category (Mutual Fund Solutions ends at 70).
    // Deterministic ordering — no reliance on which categories happen
    // to exist in the DB.
    const categoryNodes = categoryOrder.map((label) => {
        const products = categoryProducts.get(label);
        const minSort = products.reduce(
            (acc, p) => Math.min(acc, p.sortOrder ?? 100),
            Number.POSITIVE_INFINITY,
        );
        return {
            _sortOrder: Number.isFinite(minSort) ? minSort : 100,
            node: {
                type: "category",
                code: `${label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}-cat`,
                label,
                products: products.map(({ sortOrder: _s, ...rest }) => rest),  // strip helper key
            },
        };
    });
    const standaloneNodes = standalone.map((p) => ({
        _sortOrder: p.sortOrder ?? 100,
        node: (() => { const { sortOrder: _s, ...rest } = p; return rest; })(),
    }));
    [...categoryNodes, ...standaloneNodes]
        .sort((a, b) => a._sortOrder - b._sortOrder)
        .forEach(({ node }) => hierarchy.push(node));
    return hierarchy;
}

/**
 * Hydrate the live PRODUCT_MASTER from the catalogue API. Idempotent —
 * subsequent calls re-fetch and overwrite. Returns a promise that
 * resolves once hydration is complete (or rejects on fatal error).
 */
export async function hydrateProductMasterFromCatalogue(opts = {}) {
    const { force = false } = opts;
    if (_state === "ready" && !force) return { state: _state };
    if (_state === "loading" && !force) return { state: _state };

    _state = "loading";
    _error = null;
    _emit();
    try {
        const { data } = await api.get("/billing/catalogue/list", {
            params: { kind: "product", active_only: true },
        });
        const next = _buildHierarchy(data?.items || []);
        // Mutate PRODUCT_MASTER in place so existing static imports keep
        // pointing at the right array reference.
        PRODUCT_MASTER.length = 0;
        next.forEach((n) => PRODUCT_MASTER.push(n));
        _state = "ready";
        _emit();
        return { state: _state, count: PRODUCT_MASTER.length };
    } catch (err) {
        _state = "error";
        _error = err;
        _emit();
        throw err;
    }
}

/**
 * Helper — returns a flat list of every selectable leaf node in the
 * hierarchy, tagged with its parent category and product references.
 * "Selectable leaf" means: (a) a module under any product, OR (b) a
 * product that has zero modules. Used by search/filter operations.
 */
export function flattenSelectableLeaves() {
    const out = [];
    PRODUCT_MASTER.forEach((top) => {
        if (top.type === "category") {
            (top.products || []).forEach((p) => collectFromProduct(p, top, out));
        } else if (top.type === "product") {
            collectFromProduct(top, null, out);
        }
    });
    return out;
}

function collectFromProduct(product, category, out) {
    const hasModules = (product.modules || []).length > 0;
    if (!hasModules) {
        out.push({
            kind: "product",
            categoryCode: category ? category.code : null,
            categoryLabel: category ? category.label : null,
            productCode: product.code,
            productLabel: product.label,
            moduleCode: null,
            moduleLabel: null,
            code: product.code,
            label: product.label,
        });
    } else {
        (product.modules || []).forEach((m) => {
            out.push({
                kind: "module",
                categoryCode: category ? category.code : null,
                categoryLabel: category ? category.label : null,
                productCode: product.code,
                productLabel: product.label,
                moduleCode: m.code,
                moduleLabel: m.label,
                code: m.code,
                label: m.label,
            });
        });
    }
}
